import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { makeStyles } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import { FormattedMessage } from 'react-intl'
import useAudioPlayer from './hooks/useAudioPlayer'
import { evaluateInteractive } from './hooks/useLessonScoring'
import { ScenarioNodeType } from './hooks/useLessonStateMachine'
import { CapeeshColors } from '../../../../assets/ColorPalette'
import { useIsTouchDevice } from './hooks/useIsPhone'
import { playerMessages } from './CapeeshPlayer.i18n.Messages'

import TextInteraction from './interactions/TextInteraction'
import ListenButtonInteraction from './interactions/ListenButtonInteraction'
import ListenButtonWithSlider from './interactions/ListenButtonWithSlider'
import ImageInteraction from './interactions/ImageInteraction'
import MultiChoiceInteraction from './interactions/MultiChoiceInteraction'
import FillInBlankInteraction from './interactions/FillInBlankInteraction'
import AssociationPuzzleInteraction from './interactions/AssociationPuzzleInteraction'
import renderFormattedText from './utils/renderFormattedText'
import DefinitionInteraction from './interactions/DefinitionInteraction'
import WritingAiEvaluationInteraction from './interactions/WritingAiEvaluationInteraction'

const InteractiveElementType = {
    NotDefined: 0,
    MultiChoice: 1,
    FillInBlankWriting: 2,
    FillInBlankDistractors: 3,
    Text: 4,
    ListenButton: 6,
    Image: 7,
    ListenButtonWithSlider: 8,
    Definition: 10,
    AssociationPuzzle: 11,
    AssociationPuzzleSequential: 12,
    Instruction: 13,
    PronunciationSelfAssessment: 14,
    MentorAnimation: 15,
    AssociationPuzzleSequentialKeepRight: 16,
    WritingAiEvaluationInput: 17,
    AIEvaluationText: 19,
    WritingTaskEvaluationInput: 20,
}

const METADATA_ONLY_TYPES = new Set([
    InteractiveElementType.AIEvaluationText,
])

const NON_INTERACTIVE_TYPES = new Set([
    InteractiveElementType.Text,
    InteractiveElementType.ListenButton,
    InteractiveElementType.ListenButtonWithSlider,
    InteractiveElementType.Image,
    InteractiveElementType.Definition,
    InteractiveElementType.MentorAnimation,
])

const getTextAlign = (elementAlignmentType) => {
    switch (elementAlignmentType) {
        case 0: return 'left'
        case 2: return 'right'
        default: return 'center'
    }
}

function calculateReadingDelay(text) {
    if (!text) return 500
    const wordCount = text.split(/\s+/).filter(Boolean).length
    return Math.min(Math.max(wordCount * 200, 800), 3500)
}

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
    },
    scrollableArea: {
        flex: 1,
        minHeight: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
        padding: '20px 20% 0',
        boxSizing: 'border-box',
        '@media (min-width: 600px) and (orientation: landscape)': {
            padding: '12px 20% 0',
            gap: 10,
        },
        '@media (max-width: 599px)': {
            padding: '12px 8px 0',
            gap: 12,
        },
    },
    headerSection: {
        width: '100%',
        maxWidth: 480,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
        '@media (max-width: 599px)': {
            maxWidth: '100%',
        },
    },
    titleText: {
        fontFamily: 'Rubik',
        fontSize: 20,
        fontWeight: 600,
        color: '#FFFFFF',
        textAlign: 'center',
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
    },
    headerText: {
        fontFamily: 'Rubik',
        fontSize: 14,
        fontWeight: 400,
        color: 'rgba(255, 255, 255, 0.7)',
        textAlign: 'center',
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
    },
    bodyText: {
        fontFamily: 'Rubik',
        fontSize: 16,
        fontWeight: 400,
        lineHeight: '160%',
        color: '#FFFFFF',
        textAlign: 'center',
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
    },
    interactionsContainer: {
        width: '100%',
        maxWidth: 480,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
        '@media (min-width: 600px) and (orientation: landscape)': {
            gap: 10,
        },
        '@media (max-width: 599px)': {
            maxWidth: '100%',
            gap: 12,
        },
    },
    continueButtonWrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
        flexShrink: 0,
        width: '100%',
        padding: '20px 20% 20px',
        boxSizing: 'border-box',
        '@media (min-width: 600px) and (orientation: landscape)': {
            padding: '12px 20% 20px',
        },
        '@media (max-width: 599px)': {
            padding: '12px 16px 20px',
        },
    },
    undoButton: {
        padding: '10px 32px',
        borderRadius: 20,
        border: '2px solid rgba(255, 255, 255, 0.3)',
        backgroundColor: 'transparent',
        color: '#FFFFFF',
        fontFamily: 'Rubik',
        fontSize: 15,
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all 200ms ease',
        '&:hover': {
            borderColor: 'rgba(255, 255, 255, 0.5)',
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
        },
    },
    continueButton: {
        padding: '14px 48px',
        borderRadius: 28,
        border: 'none',
        backgroundColor: '#FFFFFF',
        color: '#1A1A2E',
        fontFamily: 'Rubik',
        fontSize: 16,
        fontWeight: 600,
        cursor: 'pointer',
        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.15)',
        transition: 'transform 150ms ease, opacity 200ms ease',
        '&:hover': {
            transform: 'translateY(-1px)',
        },
        '&:disabled': {
            opacity: 0.4,
            cursor: 'default',
            transform: 'none',
        },
        '@media (max-width: 599px)': {
            width: '100%',
        },
    },
    '@keyframes fadeInUp': {
        from: { opacity: 0, transform: 'translateY(10px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
    },
    fadeInWrapper: {
        animation: '$fadeInUp 400ms ease forwards',
    },
}))

/**
 * TestGenericNode — orchestrates interactive elements for test node types (7-14).
 * Renders testOptions as interactive elements, collects answers, then advances.
 *
 * For TestRule nodes (scenarioNodeType 8), interactions are revealed sequentially
 * with fade-in animations and text-length-based reading delays.
 */
const TestGenericNode = ({ node, imageMap, audioMap, onNodeComplete, isExamMode, l2LanguageId, l1Id, customLessonId, isNativeSpeaker = false }) => {
    const classes = useStyles()
    const audioPlayer = useAudioPlayer()
    const isTouchDevice = useIsTouchDevice()
    const interactionsEndRef = useRef(null)

    const [completedInteractions, setCompletedInteractions] = useState({})
    const interactiveResultsRef = useRef([])
    const [examReadyData, setExamReadyData] = useState(null)
    const containerEndRef = useRef(null)
    const writingSubmitRef = useRef(null)
    const [writingHasText, setWritingHasText] = useState(false)

    const testOptions = (node.testOptions || []).filter(
        (to) => !METADATA_ONLY_TYPES.has(to.scenarioInteractiveElementType)
    )

    const scoredOptions = useMemo(
        () => testOptions.filter((to) => !NON_INTERACTIVE_TYPES.has(to.scenarioInteractiveElementType)),
        [testOptions]
    )

    const allScorableDone = scoredOptions.every(
        (to) => completedInteractions[to.testOptionId] || (examReadyData && examReadyData.testOptionId === to.testOptionId)
    )

    // ── Sequential rendering state (TestRule only) ──
    const isSequential = node.scenarioNodeType === ScenarioNodeType.TestRule
    const [visibleCount, setVisibleCount] = useState(isSequential ? 1 : testOptions.length)
    const [displayCompleted, setDisplayCompleted] = useState({})
    const allRevealed = !isSequential || visibleCount >= testOptions.length

    // Play background audio on mount
    useEffect(() => {
        const bgAudioUrl = node.backgroundAudioUrl ? audioMap[node.backgroundAudioUrl] : null
        if (bgAudioUrl) {
            audioPlayer.playAudio(bgAudioUrl)
        }
        return () => audioPlayer.stopAudio()
    }, [])

    // Auto-scroll to latest content when new interactions appear
    useEffect(() => {
        if (interactionsEndRef.current) {
            interactionsEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })
        }
    }, [visibleCount])

    // Auto-scroll to continue button when it becomes actionable
    useEffect(() => {
        if (allScorableDone && allRevealed && containerEndRef.current) {
            containerEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })
        }
    }, [allScorableDone, allRevealed])

    const handleInteractionComplete = useCallback(
        (result) => {
            setCompletedInteractions((prev) => ({
                ...prev,
                [result.testOptionId]: true,
            }))

            const testOption = testOptions.find((to) => to.testOptionId === result.testOptionId)
            if (testOption && result.answers && result.answers.length > 0) {
                const evaluation = evaluateInteractive(testOption, result.answers, isExamMode)
                interactiveResultsRef.current.push({
                    ...evaluation,
                    answers: result.answers,
                })
            }
        },
        [testOptions]
    )

    // Called by non-interactive types (e.g. TextInteraction) when their display cycle finishes
    const handleDisplayComplete = useCallback((testOptionId, meta) => {
        setDisplayCompleted((prev) => ({ ...prev, [testOptionId]: meta || { hadAudio: false } }))
    }, [])

    // ── Sequential advancement ──
    useEffect(() => {
        if (!isSequential) return
        if (visibleCount < 1 || visibleCount > testOptions.length) return

        const currentIdx = visibleCount - 1
        const currentOption = testOptions[currentIdx]
        if (!currentOption) return

        const isNonInteractive = NON_INTERACTIVE_TYPES.has(currentOption.scenarioInteractiveElementType)

        if (isNonInteractive && displayCompleted[currentOption.testOptionId]) {
            const meta = displayCompleted[currentOption.testOptionId]
            const baseDelay = calculateReadingDelay(currentOption.text)
            const delay = meta.hadAudio ? Math.min(baseDelay, 800) : baseDelay
            const timer = setTimeout(() => {
                setVisibleCount((prev) => Math.min(prev + 1, testOptions.length))
            }, delay)
            return () => clearTimeout(timer)
        }

        if (!isNonInteractive && completedInteractions[currentOption.testOptionId]) {
            const timer = setTimeout(() => {
                setVisibleCount((prev) => Math.min(prev + 1, testOptions.length))
            }, 400)
            return () => clearTimeout(timer)
        }
    }, [isSequential, visibleCount, displayCompleted, completedInteractions, testOptions])

    const handleExamReady = useCallback((data) => {
        setExamReadyData(data)
    }, [])

    const handleExamUndo = useCallback(() => {
        if (examReadyData?.resetFn) examReadyData.resetFn()
        setExamReadyData(null)
    }, [examReadyData])

    const handleContinue = useCallback(() => {
        if (examReadyData) {
            handleInteractionComplete({
                testOptionId: examReadyData.testOptionId,
                answers: examReadyData.answers,
            })
            setExamReadyData(null)
        }

        audioPlayer.stopAudio()
        setTimeout(() => {
            onNodeComplete(
                {
                    nodeId: node.nodeId,
                    chosenAlternativeIndex: 0,
                },
                interactiveResultsRef.current
            )
        }, examReadyData ? 50 : 0)
    }, [node.nodeId, onNodeComplete, audioPlayer, examReadyData, handleInteractionComplete])

    useEffect(() => {
        const canContinue = allScorableDone && allRevealed
        if (!canContinue) return
        const onKey = (e) => {
            if (e.key !== 'Enter') return
            const tag = document.activeElement?.tagName
            if (tag === 'INPUT' || tag === 'TEXTAREA') return
            handleContinue()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [allScorableDone, allRevealed, handleContinue])

    // Extract texts by type
    const allTexts = (node.texts || []).filter(
        (t) => !isNativeSpeaker || !l2LanguageId || t.textLanguageId === l2LanguageId || !t.textLanguageId
    )
    const titleTexts = allTexts.filter((t) => t.textType === 1 || t.textType === 2)
    const headerTexts = allTexts.filter((t) => t.textType === 3)
    const bodyTexts = allTexts.filter((t) => t.textType !== 1 && t.textType !== 2 && t.textType !== 3)

    const renderInteraction = (testOption, index) => {
        const commonProps = {
            testOption,
            imageMap,
            audioMap,
            onComplete: handleInteractionComplete,
            isExamMode,
            l2LanguageId,
            textAlign: getTextAlign(testOption.elementAlignmentType),
            isNativeSpeaker,
        }

        if (isSequential) {
            commonProps.deferAudioPlayBefore = true
        }

        const isNonInteractive = NON_INTERACTIVE_TYPES.has(testOption.scenarioInteractiveElementType)
        if (isNonInteractive) {
            commonProps.onDisplayComplete = handleDisplayComplete
        }

        switch (testOption.scenarioInteractiveElementType) {
            case InteractiveElementType.Text:
                return <TextInteraction {...commonProps} />

            case InteractiveElementType.ListenButton:
                return <ListenButtonInteraction {...commonProps} />

            case InteractiveElementType.ListenButtonWithSlider:
                return <ListenButtonWithSlider {...commonProps} />

            case InteractiveElementType.Image:
                return <ImageInteraction {...commonProps} />

            case InteractiveElementType.MultiChoice:
                return <MultiChoiceInteraction {...commonProps} />

            case InteractiveElementType.FillInBlankWriting:
            case InteractiveElementType.FillInBlankDistractors:
                return <FillInBlankInteraction {...commonProps} />

            case InteractiveElementType.AssociationPuzzle:
            case InteractiveElementType.AssociationPuzzleSequential:
            case InteractiveElementType.AssociationPuzzleSequentialKeepRight:
                return <AssociationPuzzleInteraction {...commonProps} onExamReady={isExamMode ? handleExamReady : undefined} />

            case InteractiveElementType.Definition:
                return <DefinitionInteraction {...commonProps} />

            case InteractiveElementType.Instruction:
                return <TextInteraction {...commonProps} />

            case InteractiveElementType.WritingAiEvaluationInput:
            case InteractiveElementType.WritingTaskEvaluationInput:
                return (
                    <WritingAiEvaluationInteraction
                        {...commonProps}
                        nodeId={node.nodeId}
                        customLessonId={customLessonId}
                        cefrLevel={node.cefrLevel}
                        l1Id={l1Id}
                        hideSubmitButton={isExamMode}
                        submitRef={isExamMode ? writingSubmitRef : undefined}
                        onTextChange={isExamMode ? setWritingHasText : undefined}
                    />
                )

            default:
                return (
                    <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, textAlign: 'center' }}>
                        Unsupported interaction type: {testOption.scenarioInteractiveElementType}
                    </div>
                )
        }
    }

    const visibleOptions = isSequential ? testOptions.slice(0, visibleCount) : testOptions

    const isWritingNode = node.scenarioNodeType === ScenarioNodeType.WritingAiEvaluation
    const isExamWritingPending = isWritingNode && isExamMode && !allScorableDone

    return (
        <div className={classes.container}>
            <div
                className={classes.scrollableArea}
                style={{
                    ...(isWritingNode ? { padding: '20px 8% 0' } : undefined),
                    ...(isTouchDevice ? { paddingBottom: 300 } : undefined),
                }}
            >
                {/* Header texts */}
                <div className={classes.headerSection}>
                    {titleTexts.map((t, i) => (
                        <Typography key={`title-${i}`} className={classes.titleText}>
                            {renderFormattedText(t.text)}
                        </Typography>
                    ))}
                    {headerTexts.map((t, i) => (
                        <Typography key={`header-${i}`} className={classes.headerText}>
                            {renderFormattedText(t.text)}
                        </Typography>
                    ))}
                    {bodyTexts.map((t, i) => (
                        <Typography key={`body-${i}`} className={classes.bodyText}>
                            {renderFormattedText(t.text)}
                        </Typography>
                    ))}
                </div>

                {/* Interactive elements */}
                <div
                    className={classes.interactionsContainer}
                    style={isWritingNode ? { maxWidth: 'none' } : undefined}
                >
                    {visibleOptions.map((to, idx) => (
                        <div
                            key={to.testOptionId || idx}
                            className={isSequential ? classes.fadeInWrapper : undefined}
                        >
                            {renderInteraction(to, idx)}
                        </div>
                    ))}
                    <div ref={interactionsEndRef} />
                </div>
                <div ref={containerEndRef} />
            </div>

            {/* Bottom buttons — Undo (exam puzzle) + Continue */}
            <div className={classes.continueButtonWrapper}>
                {examReadyData && (
                    <button
                        className={classes.undoButton}
                        onClick={handleExamUndo}
                    >
                        <FormattedMessage {...playerMessages.undo} />
                    </button>
                )}
                <button
                    className={classes.continueButton}
                    onClick={isExamWritingPending ? () => writingSubmitRef.current?.() : handleContinue}
                    disabled={isExamWritingPending ? !writingHasText : (!allScorableDone || !allRevealed)}
                >
                    <FormattedMessage {...(isExamWritingPending ? playerMessages.submit : playerMessages.continueButton)} />
                </button>
            </div>
        </div>
    )
}

export default TestGenericNode
